import React, { useEffect, useRef, useState } from 'react'
import { MdCheckCircle } from 'react-icons/md'
import ValidationSummary from '@/components/common/ValidationSummary'
import type { Channel, CodeSent } from '@/services/authService'
import type { CodeSendResult } from '@/hooks/useLoginFlow'

/**
 * Step 2 — confirm the code (FEDEV-528).
 *
 * The code and the password used to be one form. That made a failure
 * ambiguous: a wrong code and a weak password came back together and it was
 * not obvious which had stopped you, and retrying the code meant retyping the
 * password. This screen does one thing.
 *
 * Where the code goes is decided by the server and only ever shown masked, so
 * the customer can recognise which of their addresses or numbers we hold
 * without the full value ever reaching the browser.
 */
interface Props {
  /** Destinations the server will send to, already masked. */
  channels: Channel[]
  loading: boolean
  apiError: string
  fieldErrors: Record<string, string[]>
  onRequestCode: (channel?: 'email' | 'sms') => Promise<CodeSendResult>
  onVerifyCode: (code: string) => Promise<boolean>
  onBack: () => void
}

function LoginClaimStep({
  channels,
  loading,
  apiError,
  fieldErrors,
  onRequestCode,
  onVerifyCode,
  onBack,
}: Props): React.JSX.Element {
  const [code, setCode] = useState('')
  const [sent, setSent] = useState<CodeSent | null>(null)
  const [sending, setSending] = useState(false)
  const [note, setNote] = useState('')
  const [sendError, setSendError] = useState('')
  const [error, setError] = useState('')
  const requested = useRef(false)
  const codeInput = useRef<HTMLInputElement>(null)

  // Only one way to reach them: send it without asking. With a choice, let
  // them pick, since only they know which they can actually get to.
  const onlyChannel = channels.length === 1 ? channels[0] : null

  useEffect(() => {
    if (requested.current || !onlyChannel) return
    requested.current = true

    void (async () => {
      setSending(true)
      const result = await onRequestCode(onlyChannel.type)
      setSent(result.sent)
      // Throttled, or the two-send cap reached. Saying so beats a silent code
      // box that will never receive anything.
      setSendError(result.error ?? '')
      setSending(false)
    })()
  }, [onlyChannel, onRequestCode])

  // The field is mounted from the start now, but disabled while the code is
  // being sent, so autoFocus fires too early and the focus is lost when it
  // re-enables. Put it back when the field becomes usable.
  useEffect(() => {
    if (!sending && !loading) {
      codeInput.current?.focus()
    }
  }, [sending, loading])

  const send = async (channel: 'email' | 'sms') => {
    setSending(true)
    setNote('')
    setSendError('')
    const result = await onRequestCode(channel)
    setSending(false)

    if (result.sent) {
      setSent(result.sent)
      setNote('Code sent.')
      return
    }

    // Deliberately not a blocking error: a failure here is often a
    // recently-issued code being reused, and the customer can still type the
    // one they already have. The server's reason is shown when there is one,
    // because "too many messages" and "try again" call for different things.
    setNote(
      result.error
        ? `${result.error} If you already have a code, enter it below.`
        : 'We could not send another code just now. If you already have one, enter it below.',
    )
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (code.length !== 6) {
      setError('Enter the 6-digit code.')
      return
    }

    await onVerifyCode(code)
  }

  const destination = sent?.masked
  const outOfSends = sent?.sendsRemaining === 0

  // With one channel the code is requested on mount, so the request resolves
  // after the first paint. Nothing may change shape when it does: an earlier
  // version swapped the wording and mounted the form on resolve, which read as
  // two separate screens flashing past.
  //
  // So the destination is known before the request finishes (it came from
  // check-identity), and the wording is identical before and after. What
  // settles is the disabled state and the button label, in place.
  const knownDestination = destination ?? onlyChannel?.masked

  // Paragraphs rather than one string: the "where shall we send it" case has
  // three of them, and running them together loses the break between what
  // happens next and the choice being asked for.
  const subheading = sendError && knownDestination
    // Do not claim a code is on its way when it is not. Why is in the alert.
    ? <p>We could not send a code to {knownDestination} just now.</p>
    : knownDestination
      ? <p>We&rsquo;ve sent a 6-digit code to {knownDestination}. Enter it below to confirm it&rsquo;s you.</p>
      : (
        <>
          <p>You&rsquo;re almost ready to start managing your deliveries online.</p>
          <p>
            To keep your account secure, we&rsquo;ll send you a 6-digit code to
            confirm it&rsquo;s really you.
          </p>
          <p>Choose where you&rsquo;d like to receive it:</p>
        </>
      )

  return (
    <div className="login-step login-step--accent">
      <div className="login-step-icon login-step-icon--success">
        <MdCheckCircle size={48} />
      </div>
      <h1 className="login-step-heading">Great - we found you!</h1>
      {/* A div, not a p: the copy below is several paragraphs, and a p
          cannot contain another one. */}
      <div className="login-step-subheading">
        {subheading}
      </div>

      <ValidationSummary errors={fieldErrors} message={apiError || sendError} />

      {/* Only shown when they have a genuine choice. */}
      {!onlyChannel && !sent && (
        <div className="form-group">
          {channels.map((channel) => (
            <button
              key={channel.type}
              type="button"
              className="login-channel-btn"
              onClick={() => send(channel.type)}
              disabled={sending || loading}
            >
              {channel.type === 'email'
                ? `Email a code to ${channel.masked}`
                : `Text a code to ${channel.masked}`}
            </button>
          ))}

          <p className="login-step-note">
            You'll enter the code on the next screen, then choose a password to
            finish setting up your account.
          </p>
        </div>
      )}

      {(sent || onlyChannel) && (
        <form onSubmit={submit} className="auth-form" noValidate>
          <div className="form-group">
            <label htmlFor="verificationCode" className="form-label">
              Verification code
            </label>
            <input
              id="verificationCode"
              ref={codeInput}
              type="text"
              inputMode="numeric"
              maxLength={6}
              className={`form-control form-control-lg ${error ? 'is-invalid' : ''}`}
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="6-digit code"
              disabled={loading || sending}
            />
            {error && <span className="form-text is-invalid">{error}</span>}

            {/* Stays mounted whatever happens. Swapping this control for a
                paragraph when the send limit is reached moved everything below
                it at the same moment the request resolved, which is what made
                the screen look like it had flickered between two states. It
                disables instead: two codes in total, then help rather than a
                third that will not fix a wrong number or a blocked inbox. */}
            <button
              type="button"
              className="btn btn-link"
              onClick={() => send(sent?.channel ?? onlyChannel?.type ?? 'email')}
              disabled={sending || loading || outOfSends}
            >
              {sending ? 'Sending…' : 'Send a new code'}
            </button>

            {outOfSends ? (
              <span className="form-text">
                We've sent your code twice. If it still hasn't arrived, please use the
                "trouble logging in" link below and we'll sort it out.
              </span>
            ) : (
              note && <span className="form-text">{note}</span>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-lg btn-block"
            disabled={code.length !== 6 || loading || sending}
          >
            {loading ? 'Checking…' : 'Continue'}
          </button>
        </form>
      )}

      <button type="button" className="btn btn-link" onClick={onBack}>
        Back
      </button>
    </div>
  )
}

export default LoginClaimStep
